/**
 * Shared view of quotation blocks (general + mix) — canonical implementation from store/cart.
 * Used by StoreCart (mode=client), AdminQuotationRequestDetail (admin-view/admin-edit), and acceptance view.
 * Section/fill logic: store-cart-sections.ts and store-cart-fill.ts (single source of truth).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Trash2, Package, Container, Layers, AlertTriangle, Box, Plus, Minus, Sparkles, TrendingDown, ArrowRight } from 'lucide-react';
import type { SourcingItem } from '@/lib/store-types';
import type { QuotationDocBlocks, GeneralBlockRow, MixBlock, MixBlockRow, RowLoadType } from '@/lib/store-types';
import { formatContainerType, toContainerType } from '@/lib/container-type';
import type { QuotationCartLine } from '@/hooks/use-quotation-cart';
import type { ContainerType } from '@/lib/store-types';
import { isLoadingMethodPalletized, MOQ_UNIT_BOX_NAME_PATTERN, isContainerMoq } from '@/lib/constants';
import { safeTrim } from '@/lib/utils';
import { StoreThumbnailImg } from '@/components/store/StoreThumbnailImg';
import { computeMixFillBars } from '@/pages/store/store-cart-fill';
import { buildSectionsFromBlocks, isFullContainerBlock, isFullContainerRow, computeBlockPriceSummary, type CartSection, type CartLineRow, type CartRowSource } from '@/pages/store/store-cart-sections';
import { getMixBlockSuggestionCandidates } from '@/pages/store/suggestions-candidates';

const CONTAINER_OPTIONS: { value: ContainerType; label: string }[] = [
  { value: 20, label: '20ft Dry' },
  { value: 40, label: '40ft Dry' },
  { value: '20REF', label: '20ft Refer' },
  { value: '40REF', label: '40ft Refer' },
];

const PERSIST_DEBOUNCE_MS = 1000;
const WEEKS_INPUT_DEBOUNCE_MS = 800;

/** Parsea texto a semanas (número entero): acepta dígitos o palabras en español (uno, dos, ... diez). */
function parseWeeksFromText(text: string): number | undefined {
  const t = text.trim().toLowerCase();
  if (t === '') return undefined;
  const n = Number(t.replace(',', '.'));
  if (!Number.isNaN(n) && n >= 0 && Number.isFinite(n)) return Math.round(n);
  const words: Record<string, number> = { uno: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10 };
  return words[t] ?? undefined;
}

/** Props required when mode is 'client' (cart editing). */
export interface QuotationBlocksViewClientProps {
  patchBlocks: (b: QuotationDocBlocks) => Promise<void>;
  add: (line: QuotationCartLine, options?: { mixBlockIndex?: number; forceNewContainer?: boolean }) => Promise<void>;
  updateQuantity: (sourcingItemId: string, q: number) => Promise<void>;
  remove: (sourcingItemId: string) => Promise<void>;
  toast: (t: { title: string; description?: string; variant?: 'destructive' }) => void;
  getDefaultLoadType: (sourcingItemId: string) => RowLoadType;
  updateMixRowLoadType: (blockIndex: number, rowIndex: number, value: RowLoadType) => void;
  allSourcingItems: SourcingItem[];
  loadingMethods: { id: string; name: string }[];
  brands: { id: string; name: string }[];
  origins: { id: string; name: string }[];
  originPorts: { id: string; name: string; origin_id?: string }[];
  moqUnits: { id: string; name: string }[];
  /** For mix block suggestions: filter by family (first item's family + mix options). */
  itemIdToFamilyId?: Record<string, string>;
  mixOptions?: { family_id: string; related_family_id: string }[];
}

/** Props for admin modes: block updates, fill bars. */
export interface QuotationBlocksViewAdminProps {
  /** When admin-edit: persist block updates (e.g. specifications) */
  onPatchBlocks?: (blocks: QuotationDocBlocks) => void;
  /** Optional: for fill bars in mix blocks when not using clientProps (admin view) */
  allSourcingItems?: SourcingItem[];
  loadingMethods?: { id: string; name: string }[];
  /** Mostrar precio/caja en USD por línea cuando la cotización está cerrada (cotizado/finished). Usa price_per_box_approx de los bloques. */
  showFinalLinePrice?: boolean;
}

export type QuotationBlocksViewMode = 'client' | 'admin-view' | 'admin-edit';

export interface QuotationBlocksViewProps {
  /** Request with blocks and optional status (used to allow container type edit only when admin + en_revision). */
  request: { blocks?: QuotationDocBlocks; status?: string } | null;
  /** Line items; must match blocks when present (same source as buildSectionsFromBlocks). */
  items: QuotationCartLine[];
  mode: QuotationBlocksViewMode;
  /** Override read-only; when not set, client=false for client mode, true for admin-view, configurable for admin-edit */
  readOnly?: boolean;
  /** Required when mode === 'client' */
  clientProps?: QuotationBlocksViewClientProps;
  /** Used when mode is admin-view or admin-edit */
  adminProps?: QuotationBlocksViewAdminProps;
  /** When true (e.g. cart after non-draft): render only the "Agregar producto o contenedor" block. */
  showOnlyAddBlock?: boolean;
}

/** Partial row fields for admin quotation (price per box, transport days). */
export type RowQuotationFields = {
  price_per_box_approx?: number | undefined;
  estimated_production_days?: number | undefined;
  estimated_arrival_days?: number | undefined;
};

/** Single line card: thumbnail, name, quantity (or read-only), optional price ref, remove, optional quotation fields (admin). */
function CartLineCard({
  line,
  quantity,
  unitLabel,
  minQuantity = 1,
  updateQuantity,
  remove,
  rowLoadType,
  defaultLoadType,
  onRowLoadTypeChange,
  showLoadTypeSelector = false,
  disabled = false,
  priceReference,
  onPriceReferenceBlur,
  showPriceRef = false,
  priceRefEditing = true,
  pricePerBoxApprox,
  estimatedProductionDays,
  estimatedArrivalDays,
  showQuotationFields = false,
  showQuotationDaysOnRow = false,
  onQuotationFieldsBlur,
  hideMoqInSubtitle = false,
  finalReadOnlyView = false,
}: {
  line: QuotationCartLine;
  quantity: number;
  unitLabel: string;
  minQuantity?: number;
  updateQuantity?: (sourcingItemId: string, q: number) => Promise<void>;
  remove?: (sourcingItemId: string) => Promise<void>;
  rowLoadType?: RowLoadType;
  defaultLoadType?: RowLoadType;
  onRowLoadTypeChange?: (value: RowLoadType) => void;
  /** When true (e.g. mix block rows), always show load type display (view-only or editable). */
  showLoadTypeSelector?: boolean;
  disabled?: boolean;
  priceReference?: number;
  onPriceReferenceBlur?: (value: number | undefined) => void;
  showPriceRef?: boolean;
  priceRefEditing?: boolean;
  pricePerBoxApprox?: number;
  estimatedProductionDays?: number;
  estimatedArrivalDays?: number;
  showQuotationFields?: boolean;
  /** When true, show days inputs (general block only; mix block shows days at block level). */
  showQuotationDaysOnRow?: boolean;
  onQuotationFieldsBlur?: (fields: RowQuotationFields) => void;
  /** When true (e.g. producto por contenedor), omit "Mín. X Unit" under the name — quantity + unit already shown in the row input. */
  hideMoqInSubtitle?: boolean;
  /** Vista de cotización final (cotizado/finished): esconder controles e inputs y mostrar todo como información. */
  finalReadOnlyView?: boolean;
}) {
  const [localQty, setLocalQty] = useState(() => Math.max(quantity, minQuantity));
  const [localPriceRef, setLocalPriceRef] = useState(() =>
    priceReference != null ? String(priceReference) : ''
  );
  const [localPricePerBox, setLocalPricePerBox] = useState(() =>
    pricePerBoxApprox != null ? String(pricePerBoxApprox) : ''
  );
  const [localDaysProd, setLocalDaysProd] = useState(() =>
    estimatedProductionDays != null ? String(Math.round(estimatedProductionDays / 7)) : ''
  );
  const [localDaysArrival, setLocalDaysArrival] = useState(() =>
    estimatedArrivalDays != null ? String(Math.round(estimatedArrivalDays / 7)) : ''
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [removing, setRemoving] = useState(false);
  const effectiveLoadType = rowLoadType ?? defaultLoadType ?? 'floor';
  const canChangeLoadType = !!onRowLoadTypeChange && showLoadTypeSelector;

  const handleRemove = useCallback(() => {
    if (!remove || disabled) return;
    setRemoving(true);
    remove(line.sourcingItemId);
  }, [remove, disabled, line.sourcingItemId]);

  useEffect(() => {
    setLocalQty((prev) => {
      const fromServer = quantity;
      const min = minQuantity;
      if (fromServer < min) return min;
      return fromServer;
    });
  }, [quantity, minQuantity]);

  useEffect(() => {
    setLocalPriceRef(priceReference != null ? String(priceReference) : '');
  }, [priceReference]);
  useEffect(() => {
    setLocalPricePerBox(pricePerBoxApprox != null ? String(pricePerBoxApprox) : '');
  }, [pricePerBoxApprox]);
  useEffect(() => {
    setLocalDaysProd(estimatedProductionDays != null ? String(Math.round(estimatedProductionDays / 7)) : '');
  }, [estimatedProductionDays]);
  useEffect(() => {
    setLocalDaysArrival(estimatedArrivalDays != null ? String(Math.round(estimatedArrivalDays / 7)) : '');
  }, [estimatedArrivalDays]);

  const applyQuantity = useCallback(
    (newQty: number) => {
      const clamped = Math.max(minQuantity, newQty);
      setLocalQty(clamped);
      if (updateQuantity) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = null;
        updateQuantity(line.sourcingItemId, clamped);
      }
    },
    [line.sourcingItemId, minQuantity, updateQuantity]
  );

  const handleQuantityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = Number(e.target.value);
      if (Number.isNaN(v)) return;
      const clamped = Math.max(minQuantity, v);
      setLocalQty(clamped);
      if (!updateQuantity) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        updateQuantity(line.sourcingItemId, clamped);
      }, PERSIST_DEBOUNCE_MS);
    },
    [line.sourcingItemId, minQuantity, updateQuantity]
  );

  /** +5 / -5: ajuste rápido; el detalle (+1, etc.) se hace manual en el input. */
  const handleQuantityStep = useCallback(
    (delta: number) => {
      if (!updateQuantity || disabled) return;
      const next = Math.max(minQuantity, localQty + delta);
      applyQuantity(next);
    },
    [localQty, minQuantity, updateQuantity, disabled, applyQuantity]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handlePriceRefBlur = () => {
    if (onPriceReferenceBlur == null) return;
    const v = localPriceRef.trim();
    if (v === '') {
      onPriceReferenceBlur(undefined);
      return;
    }
    const num = Number(v);
    if (!Number.isNaN(num) && num >= 0 && Number.isFinite(num)) onPriceReferenceBlur(num);
  };

  const handleQuotationFieldsBlur = () => {
    if (!onQuotationFieldsBlur) return;
    const price = localPricePerBox.trim() === '' ? undefined : Number(localPricePerBox);
    const prodWeeks = parseWeeksFromText(localDaysProd);
    const arrWeeks = parseWeeksFromText(localDaysArrival);
    if (price !== undefined && Number.isNaN(price)) return;
    onQuotationFieldsBlur({
      price_per_box_approx: price,
      estimated_production_days: prodWeeks !== undefined ? prodWeeks * 7 : undefined,
      estimated_arrival_days: arrWeeks !== undefined ? arrWeeks * 7 : undefined,
    });
  };

  return (
    <li
      className={`group flex flex-wrap sm:flex-nowrap items-center gap-3 rounded-xl border border-zinc-200 bg-white p-3 shadow-sm hover:shadow-md hover:border-zinc-300 transition-all duration-200 ${
        removing ? 'opacity-0 scale-95 pointer-events-none' : ''
      }`}
    >
      <Link
        to={`/store/product/${line.slug}`}
        className="shrink-0 w-16 h-14 flex items-center justify-center rounded-lg border border-zinc-200 bg-zinc-50 overflow-hidden group-hover:border-zinc-300 transition-colors"
      >
        {line.imageUrl ? (
          <StoreThumbnailImg
            coverUrl={line.imageUrl}
            size={{ width: 64, height: 56 }}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <Package className="h-6 w-6 text-zinc-300" />
        )}
      </Link>
      <div className="min-w-0 flex-1">
        <Link
          to={`/store/product/${line.slug}`}
          className="font-semibold text-zinc-900 hover:text-primary text-sm line-clamp-1 group-hover:underline"
        >
          {line.name}
        </Link>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0 text-[11px] text-zinc-500 mt-0.5">
          {[line.brandName, line.originName, line.salesFormat].filter(Boolean).length > 0 && (
            <span className="truncate">{[line.brandName, line.originName, line.salesFormat].filter(Boolean).join(' · ')}</span>
          )}
          {!hideMoqInSubtitle && (
            <span className="flex items-center gap-0.5 shrink-0">
              <Package className="h-2.5 w-2.5" />
              Mín. {line.moqValue.toLocaleString()} {line.moqUnitLabel}
            </span>
          )}
        </div>
      </div>
      {/* Columna central: unidades por caja; cajas por pallet (paletizada) o cajas por contenedor (a piso) */}
      {(showLoadTypeSelector || rowLoadType != null || defaultLoadType != null) && (
        <div className="shrink-0 flex flex-col gap-0.5 text-[11px] text-zinc-600 border-l border-zinc-200 pl-3 min-w-[8rem]">
          <span className="whitespace-nowrap">
            Unid./caja: {line.unitsPerBox != null ? line.unitsPerBox.toLocaleString() : '—'}
          </span>
          {effectiveLoadType === 'pallets' && (
            <span className="whitespace-nowrap">
              Cajas/pallet: {line.boxesPerPallet != null ? line.boxesPerPallet.toLocaleString() : '—'}
            </span>
          )}
          {effectiveLoadType === 'floor' && (
            <span className="whitespace-nowrap">
              Cajas/cont.: {line.boxesPerContainer != null ? line.boxesPerContainer.toLocaleString() : '—'}
            </span>
          )}
          {!canChangeLoadType && (
            <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] font-medium text-zinc-700">
              <span className="uppercase tracking-wide">Carga</span>
              <span>·</span>
              <span>{effectiveLoadType === 'pallets' ? 'Paletizada' : 'A piso'}</span>
            </span>
          )}
        </div>
      )}
      {canChangeLoadType && (
        <div className="shrink-0 flex items-center gap-2 min-w-[11rem] w-[13rem]">
          <span className="text-[11px] text-zinc-500 font-medium whitespace-nowrap">Carga</span>
          <span className="inline-flex rounded-md border border-zinc-200 bg-zinc-50/80 p-0.5 flex-1 min-w-0">
            <button
              type="button"
              onClick={() => onRowLoadTypeChange?.('pallets')}
              disabled={!onRowLoadTypeChange || disabled}
              className={`flex-1 min-w-0 px-2 py-1 text-[11px] rounded transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap ${effectiveLoadType === 'pallets' ? 'bg-sky-600 text-white shadow-sm' : 'text-zinc-600 hover:bg-zinc-200'} disabled:opacity-70 disabled:cursor-default disabled:hover:bg-transparent`}
              title="Paletizada"
            >
              <Layers className="h-3 w-3 shrink-0" />
              <span>Paletizada</span>
            </button>
            <button
              type="button"
              onClick={() => onRowLoadTypeChange?.('floor')}
              disabled={!onRowLoadTypeChange || disabled}
              className={`flex-1 min-w-0 px-2 py-1 text-[11px] rounded transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap ${effectiveLoadType === 'floor' ? 'bg-sky-600 text-white shadow-sm' : 'text-zinc-600 hover:bg-zinc-200'} disabled:opacity-70 disabled:cursor-default disabled:hover:bg-transparent`}
              title="A piso"
            >
              <Box className="h-3 w-3 shrink-0" />
              <span>A piso</span>
            </button>
          </span>
        </div>
      )}
      {showPriceRef && (
        <div
          className={
            finalReadOnlyView
              ? 'flex flex-col items-end gap-0.5 shrink-0 min-w-[9rem]'
              : 'flex items-center gap-2 shrink-0'
          }
        >
          <span className="text-[11px] text-zinc-500">
            Precio/caja
          </span>
          {priceRefEditing && onPriceReferenceBlur ? (
            <Input
              type="number"
              min={0}
              step={0.01}
              placeholder="—"
              className="w-24 h-9 text-sm"
              value={localPriceRef}
              onChange={(e) => setLocalPriceRef(e.target.value)}
              onBlur={handlePriceRefBlur}
            />
          ) : (
            <span
              className={
                finalReadOnlyView
                  ? 'text-base font-semibold w-full text-right tabular-nums text-zinc-900'
                  : 'text-sm font-medium w-24 text-right tabular-nums'
              }
            >
              {pricePerBoxApprox != null
                ? `USD ${pricePerBoxApprox.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : '—'}
            </span>
          )}
        </div>
      )}
      {showQuotationFields && onQuotationFieldsBlur && (
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-zinc-500">Precio/caja</span>
            <Input
              type="number"
              min={0}
              step={0.01}
              placeholder="—"
              className="w-20 h-9 text-sm"
              value={localPricePerBox}
              onChange={(e) => setLocalPricePerBox(e.target.value)}
              onBlur={handleQuotationFieldsBlur}
            />
          </div>
          {showQuotationDaysOnRow && (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-zinc-500">Semanas de producción</span>
                <Input
                  type="text"
                  placeholder="—"
                  className="min-w-[6rem] w-24 h-10 text-base"
                  value={localDaysProd}
                  onChange={(e) => setLocalDaysProd(e.target.value)}
                  onBlur={handleQuotationFieldsBlur}
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-zinc-500">Semanas de transporte</span>
                <Input
                  type="text"
                  placeholder="—"
                  className="min-w-[6rem] w-24 h-10 text-base"
                  value={localDaysArrival}
                  onChange={(e) => setLocalDaysArrival(e.target.value)}
                  onBlur={handleQuotationFieldsBlur}
                />
              </div>
            </>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 shrink-0 min-w-[10rem] justify-end">
        {finalReadOnlyView ? (
          <span className="text-sm font-medium text-zinc-900 tabular-nums">
            {localQty.toLocaleString()} <span className="text-xs text-zinc-500">{unitLabel}</span>
          </span>
        ) : (
          <>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => handleQuantityStep(-5)}
                disabled={disabled || !updateQuantity || localQty <= minQuantity}
                aria-label="Restar 5"
                title="Restar 5"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Input
                type="number"
                min={minQuantity}
                value={localQty}
                onChange={handleQuantityChange}
                disabled={disabled || !updateQuantity}
                className="w-20 h-9 text-sm"
                title="Cantidad (editar manualmente para +1 u otro valor)"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => handleQuantityStep(5)}
                disabled={disabled || !updateQuantity}
                aria-label="Sumar 5"
                title="Sumar 5"
              >
                <Plus className="h-4 w-4" />
              </Button>
              <span className="text-xs text-zinc-500 min-w-[7.5rem] whitespace-nowrap ml-0.5">{unitLabel}</span>
            </div>
            {remove && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-zinc-400 hover:text-red-600 transition-colors"
                onClick={handleRemove}
                aria-label="Quitar"
                disabled={disabled || removing}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </>
        )}
      </div>
    </li>
  );
}

/** Contexto del buscador: mix = añadir a bloque existente; new-mix = crear bloque (mixto o contenedor completo). */
export type BlockProductSearchContext = 'mix' | 'new-mix';

/** Buscador único para añadir productos (mixto o contenedor completo según MOQ del producto). */
function BlockProductSearch({
  context,
  mixBlock,
  mixBlockIndex,
  allItems,
  orderIds,
  moqUnits,
  brands,
  origins,
  originPorts,
  add,
  toast,
  readOnly,
  label,
  placeholder,
  listBorderClass = 'border-zinc-200',
  listHoverClass = 'hover:bg-zinc-100',
  onAddedFromNewMix,
}: {
  context: BlockProductSearchContext;
  mixBlock?: MixBlock;
  mixBlockIndex?: number;
  allItems: SourcingItem[];
  orderIds: string[];
  moqUnits: { id: string; name: string }[];
  brands: { id: string; name: string }[];
  origins: { id: string; name: string }[];
  originPorts: { id: string; name: string; origin_id?: string }[];
  add?: (line: QuotationCartLine, options?: { mixBlockIndex?: number; forceNewContainer?: boolean }) => Promise<void>;
  toast?: (t: { title: string; description?: string; variant?: 'destructive' }) => void;
  readOnly?: boolean;
  label: string;
  placeholder: string;
  listBorderClass?: string;
  listHoverClass?: string;
  /** Called after adding from context "new-mix" (new container created); used to scroll to the new section. */
  onAddedFromNewMix?: () => void;
}) {
  const [search, setSearch] = useState('');
  const [focused, setFocused] = useState(false);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight?: number;
  } | null>(null);

  // Primitives so each block's search instance filters by its own origin/port/supplier (no shared ref).
  const mixOriginId = mixBlock?.origin_id ?? '';
  const mixPortId = mixBlock?.origin_port_id ?? '';
  const mixSupplierId = mixBlock?.supplier_id ?? '';

  // Open dropdown upward when near bottom of viewport (e.g. "Añadir producto o contenedor" section) so it stays visible.
  const updateDropdownPosition = useCallback(() => {
    const el = inputContainerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownMaxH = 208; // max-h-52 ≈ 13rem
    const openUpward = spaceBelow < dropdownMaxH + 24 && spaceAbove > spaceBelow;
    const width = Math.max(rect.width, 200);
    if (openUpward) {
      setDropdownPosition({
        bottom: viewportH - rect.top + 4,
        left: rect.left,
        width,
        maxHeight: Math.min(dropdownMaxH, spaceAbove - 16),
      });
    } else {
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width,
        maxHeight: Math.min(dropdownMaxH, spaceBelow - 16),
      });
    }
  }, []);

  // Clear search when user focuses this input so conditions are reloaded for this block (fixes wrong filter on 2nd+ blocks).
  const handleFocus = useCallback(() => {
    setSearch('');
    setFocused(true);
  }, []);
  const handleBlur = useCallback(() => {
    setTimeout(() => setFocused(false), 150);
  }, []);

  const candidateItems = useMemo(() => {
    const q = safeTrim(search).toLowerCase();
    const filtered = allItems.filter((item) => {
      if (orderIds.includes(item.id)) return false;
      const moqName = moqUnits.find((m) => m.id === item.moq_unit_id)?.name ?? '';

      if (context === 'new-mix') {
        // Allow both: container MOQ → full-container block; box/pallet → new mix block.
      } else {
        if (isContainerMoq(moqName)) return false;
        const moqIsPalletOrCajas = isLoadingMethodPalletized(moqName) || MOQ_UNIT_BOX_NAME_PATTERN.test(moqName);
        if (!moqIsPalletOrCajas) return false;
      }

      if (context === 'mix') {
        if (item.origin_id !== mixOriginId || (item.origin_port_id ?? '') !== mixPortId) return false;
        if (mixSupplierId && (item.supplier_id ?? '') !== mixSupplierId) return false;
      }

      if (q) {
        const nameMatch = item.internal_name?.toLowerCase().includes(q) || item.slug?.toLowerCase().includes(q);
        const originName = origins?.find((o) => o.id === item.origin_id)?.name?.toLowerCase() ?? '';
        const portName = originPorts?.find((p) => p.id === item.origin_port_id)?.name?.toLowerCase() ?? '';
        const brandName = brands?.find((b) => b.id === item.brand_id)?.name?.toLowerCase() ?? '';
        if (!nameMatch && !originName.includes(q) && !portName.includes(q) && !brandName.includes(q)) return false;
      }
      return true;
    });
    return filtered.slice(0, 20);
  }, [allItems, orderIds, search, moqUnits, origins, originPorts, brands, context, mixOriginId, mixPortId, mixSupplierId]);

  const handleAdd = (item: SourcingItem) => {
    if (!add || !toast) return;
    const brand = brands.find((b) => b.id === item.brand_id);
    const origin = origins.find((o) => o.id === item.origin_id);
    const moqUnit = moqUnits.find((m) => m.id === item.moq_unit_id);
    const minMoq = Math.round(Number(item.moq_value) || 1);
    const line: QuotationCartLine = {
      sourcingItemId: item.id,
      slug: item.slug,
      name: item.internal_name,
      moqValue: minMoq,
      moqUnitId: item.moq_unit_id ?? '',
      moqUnitLabel: moqUnit?.name ?? '',
      quantity: minMoq,
      salesFormat: item.sales_format,
      imageUrl: item.packshot_image,
      brandName: brand?.name,
      originName: origin?.name,
      unitsPerBox: item.units_per_box ?? undefined,
      boxesPerPallet: item.boxes_per_pallet ?? undefined,
      boxesPerContainer: item.boxes_per_container ?? undefined,
    };
    // Desde "Agregar producto o contenedor" (new-mix) siempre se crea un contenedor nuevo: forceNewContainer + sin mixBlockIndex.
    const options =
      context === 'mix' && typeof mixBlockIndex === 'number'
        ? { mixBlockIndex }
        : context === 'new-mix'
          ? { forceNewContainer: true }
          : undefined;
    setFocused(false);
    add(line, options)
      .then(() => {
        if (context === 'new-mix' && isContainerMoq(moqUnit?.name ?? '')) {
          toast({ title: 'Bloque completo añadido', description: item.internal_name ?? '' });
        } else if (context === 'new-mix') {
          toast({ title: 'Bloque mixto creado', description: 'Añade más productos desde el bloque (mismo origen, puerto y proveedor).' });
        } else {
          toast({ title: 'Añadido al mix', description: item.internal_name ?? '' });
        }
        if (context === 'new-mix') onAddedFromNewMix?.();
      })
      .catch(() => toast({ title: 'Error al añadir', variant: 'destructive' }));
    setSearch('');
  };

  const showList = candidateItems.length > 0 && (focused || search.length > 0);

  // When dropdown is shown, position it from the input; update on scroll/resize so it stays aligned. Portal so it's never clipped by sibling sections.
  useEffect(() => {
    if (!showList) {
      setDropdownPosition(null);
      return;
    }
    updateDropdownPosition();
    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [showList, updateDropdownPosition]);

  if (readOnly || !add) return null;

  const dropdownContent =
    showList && dropdownPosition
      ? ReactDOM.createPortal(
          <div
            className={`fixed z-[100] overflow-auto rounded-lg border bg-white shadow-lg py-1 ${listBorderClass}`}
            style={{
              ...(dropdownPosition.top != null ? { top: dropdownPosition.top } : { bottom: dropdownPosition.bottom }),
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              maxHeight: dropdownPosition.maxHeight ?? 208,
            }}
            onMouseDown={(e) => e.preventDefault()}
            role="listbox"
          >
            <ul className={`${listBorderClass}`}>
              {candidateItems.map((item) => {
                const originName = origins?.find((o) => o.id === item.origin_id)?.name ?? '';
                const moqUnit = moqUnits.find((m) => m.id === item.moq_unit_id);
                const moqVal = Math.round(Number(item.moq_value) || 0) || 1;
                const label = [originName, item.internal_name, `${moqVal.toLocaleString()} ${moqUnit?.name ?? ''}`, item.sales_format].filter(Boolean).join(' · ');
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`w-full text-left px-3 py-2 text-sm truncate ${listHoverClass}`}
                      onClick={() => handleAdd(item)}
                      title={label}
                    >
                      {label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={inputContainerRef} className="relative">
      <label className="text-xs font-medium text-zinc-600 block mb-1">{label}</label>
      <Input
        placeholder={placeholder}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="text-sm"
      />
      {dropdownContent}
    </div>
  );
}

/** Máximo de ítems mostrados en el panel de sugerencias; si hay más, se indica usar la búsqueda. */
const SUGGESTIONS_VISIBLE_MAX = 7;

/** Panel "Te sugerimos añadir" por contenedor. Filtros (origen, puerto, proveedor, load_type) derivados del primer ítem del contenedor. */
function MixBlockSuggestionsPanel({
  block,
  mixBlockIndex,
  sectionRows,
  allItems,
  moqUnits,
  loadingMethods,
  brands,
  origins,
  originPorts,
  add,
  toast,
  readOnly = false,
  itemIdToFamilyId,
  mixOptions,
}: {
  block: MixBlock;
  mixBlockIndex: number;
  sectionRows: CartLineRow[];
  allItems: SourcingItem[];
  moqUnits: { id: string; name: string }[];
  loadingMethods: { id: string; name: string }[];
  brands: { id: string; name: string }[];
  origins: { id: string; name: string }[];
  originPorts: { id: string; name: string; origin_id?: string }[];
  add?: (line: QuotationCartLine, options?: { mixBlockIndex?: number }) => Promise<void>;
  toast?: (t: { title: string; description?: string; variant?: 'destructive' }) => void;
  readOnly?: boolean;
  itemIdToFamilyId?: Record<string, string>;
  mixOptions?: { family_id: string; related_family_id: string }[];
}) {
  const [search, setSearch] = useState('');
  const [addingId, setAddingId] = useState<string | null>(null);
  const orderIds = useMemo(() => sectionRows.map((r) => r.line.sourcingItemId), [sectionRows]);
  const firstItem = useMemo(() => {
    const firstId = sectionRows[0]?.line?.sourcingItemId;
    if (!firstId) return null;
    return allItems.find((i) => i.id === firstId) ?? null;
  }, [sectionRows, allItems]);

  const allowedFamilyIds = useMemo(() => {
    if (!firstItem || !itemIdToFamilyId || !mixOptions?.length) return undefined;
    const firstFamilyId = itemIdToFamilyId[firstItem.id];
    if (!firstFamilyId) return undefined;
    const set = new Set<string>([firstFamilyId]);
    mixOptions.forEach((r) => {
      if (r.family_id === firstFamilyId) set.add(r.related_family_id);
      if (r.related_family_id === firstFamilyId) set.add(r.family_id);
    });
    return set;
  }, [firstItem, itemIdToFamilyId, mixOptions]);

  const baseCandidates = useMemo(
    () =>
      getMixBlockSuggestionCandidates(
        firstItem,
        orderIds,
        allItems,
        moqUnits,
        loadingMethods,
        block.supplier_id,
        itemIdToFamilyId != null && allowedFamilyIds != null
          ? { itemIdToFamilyId, allowedFamilyIds }
          : undefined
      ),
    [firstItem, orderIds, allItems, moqUnits, loadingMethods, block.supplier_id, itemIdToFamilyId, allowedFamilyIds]
  );

  const filteredCandidates = useMemo(() => {
    const q = safeTrim(search).toLowerCase();
    if (!q) return baseCandidates;
    return baseCandidates.filter((item) => {
      const nameMatch = item.internal_name?.toLowerCase().includes(q) || item.slug?.toLowerCase().includes(q);
      const originName = origins?.find((o) => o.id === item.origin_id)?.name?.toLowerCase() ?? '';
      const portName = originPorts?.find((p) => p.id === item.origin_port_id)?.name?.toLowerCase() ?? '';
      const brandName = brands?.find((b) => b.id === item.brand_id)?.name?.toLowerCase() ?? '';
      return nameMatch || originName.includes(q) || portName.includes(q) || brandName.includes(q);
    });
  }, [baseCandidates, search, origins, originPorts, brands]);

  const visibleItems = useMemo(() => filteredCandidates.slice(0, SUGGESTIONS_VISIBLE_MAX), [filteredCandidates]);
  const hasMore = filteredCandidates.length > SUGGESTIONS_VISIBLE_MAX;

  /** Contenedor lleno (peso, m³ o pallets): no se pueden añadir más ítems; mostramos overlay sobre la lista. */
  const fillBars = useMemo(() => {
    if (isFullContainerBlock(block)) return null;
    const fillRows = sectionRows.map((r, idx) => ({
      line: { sourcingItemId: r.line.sourcingItemId, moqUnitLabel: r.line.moqUnitLabel },
      quantity: r.quantity,
      unitLabel: r.unitLabel,
      rowLoadType: block.rows?.[idx]?.row_load_type ?? r.rowLoadType,
    }));
    return computeMixFillBars(block, fillRows, allItems, loadingMethods);
  }, [block, sectionRows, allItems, loadingMethods]);
  const isContainerFull = fillBars != null && (fillBars.overWeight || fillBars.overSpace || fillBars.overPallets);

  const handleAdd = useCallback(
    (item: SourcingItem) => {
      if (!add || !toast) return;
      const brand = brands.find((b) => b.id === item.brand_id);
      const origin = origins.find((o) => o.id === item.origin_id);
      const moqUnit = moqUnits.find((m) => m.id === item.moq_unit_id);
      const minMoq = Math.round(Number(item.moq_value) || 1);
      const line: QuotationCartLine = {
        sourcingItemId: item.id,
        slug: item.slug,
        name: item.internal_name,
        moqValue: minMoq,
        moqUnitId: item.moq_unit_id ?? '',
        moqUnitLabel: moqUnit?.name ?? '',
        quantity: minMoq,
        salesFormat: item.sales_format,
        imageUrl: item.packshot_image,
        brandName: brand?.name,
        originName: origin?.name,
        unitsPerBox: item.units_per_box ?? undefined,
        boxesPerPallet: item.boxes_per_pallet ?? undefined,
        boxesPerContainer: item.boxes_per_container ?? undefined,
      };
      setAddingId(item.id);
      add(line, { mixBlockIndex })
        .then(() => toast({ title: 'Añadido al mix', description: item.internal_name ?? '' }))
        .catch(() => toast({ title: 'Error al añadir', variant: 'destructive' }))
        .finally(() => setTimeout(() => setAddingId(null), 400));
    },
    [add, toast, brands, origins, moqUnits, mixBlockIndex]
  );

  if (readOnly || !add) return null;

  return (
    <div className="w-full max-w-full rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-white to-primary/5 overflow-hidden shadow-lg flex flex-col min-h-0">
      <div className="bg-gradient-to-r from-primary to-primary/80 px-4 py-3 min-h-[4.25rem] flex items-center text-primary-foreground shrink-0">
        <div className="flex items-center gap-2 w-full">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm flex items-center gap-1.5">
              Te sugerimos añadir
              <Badge variant="secondary" className="bg-white/30 text-primary-foreground border-white/20 text-[10px] font-semibold">
                <TrendingDown className="h-2.5 w-2.5 mr-0.5" />
                Mejor precio
              </Badge>
            </h3>
            <p className="text-[11px] opacity-90 mt-0.5">Mismo origen, puerto y proveedor. Busca o elige abajo.</p>
          </div>
        </div>
      </div>
      <div className="p-3 flex flex-col flex-1 min-h-0 relative">
        <Input
          placeholder="Buscar producto, origen o puerto..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="text-sm mb-2 shrink-0"
          disabled={isContainerFull}
        />
        <div className={`relative flex-1 min-h-0 flex flex-col ${isContainerFull ? 'pointer-events-none' : ''}`}>
          <ul className={`space-y-2 overflow-auto flex-1 min-h-0 pr-0.5 ${isContainerFull ? 'blur-[2px] select-none' : ''}`}>
            {visibleItems.length === 0 ? (
              <li className="rounded-xl border-2 border-dashed border-primary/20 bg-primary/5 px-3 py-4 text-center">
                <p className="text-xs text-zinc-600">
                  {safeTrim(search)
                    ? 'No hay productos que coincidan con tu búsqueda.'
                    : 'No hay más sugerencias para este contenedor (mismo origen, puerto y proveedor).'}
                </p>
              </li>
            ) : (
              visibleItems.map((item) => {
                const isAdding = addingId === item.id;
                const originName = origins?.find((o) => o.id === item.origin_id)?.name ?? '';
                const moqUnit = moqUnits.find((m) => m.id === item.moq_unit_id);
                const minMoq = Math.round(Number(item.moq_value) || 0) || 1;
                const subLabel = [originName, `Mín. ${minMoq.toLocaleString()} ${moqUnit?.name ?? ''}`, item.sales_format].filter(Boolean).join(' – ');
                return (
                  <li key={item.id} className={`transition-all ${isAdding ? 'scale-95 opacity-50' : ''}`}>
                    <div className="flex items-center gap-2 rounded-xl border-2 border-zinc-200 bg-white p-2.5 hover:border-primary/40 hover:shadow-md transition-all">
                      <Link
                        to={`/store/product/${item.slug}`}
                        className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg border border-zinc-100 overflow-hidden bg-zinc-50"
                      >
                        {item.packshot_image ? (
                          <StoreThumbnailImg
                            coverUrl={item.packshot_image}
                            size={{ width: 40, height: 40 }}
                            className="max-h-full max-w-full object-contain"
                          />
                        ) : (
                          <Package className="h-4 w-4 text-zinc-300" />
                        )}
                      </Link>
                      <div className="flex-1 min-w-0">
                        <Link to={`/store/product/${item.slug}`} className="text-xs font-medium text-zinc-900 line-clamp-2 hover:text-primary">
                          {item.internal_name}
                        </Link>
                        {subLabel && (
                          <p className="text-[10px] text-zinc-500 mt-0.5 line-clamp-1" title={subLabel}>
                            {subLabel}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0 h-7 px-2 gap-1 text-xs bg-primary-foreground text-primary border-primary/20 hover:bg-primary hover:text-primary-foreground"
                        onClick={() => handleAdd(item)}
                        disabled={isAdding}
                      >
                        {isAdding ? '✓' : <><span>Añadir</span><ArrowRight className="h-3 w-3" /></>}
                      </Button>
                    </div>
                  </li>
                );
              })
            )}
            {hasMore && visibleItems.length > 0 && (
              <li className="rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 px-3 py-2.5 text-center">
                <p className="text-xs text-zinc-600">
                  Más productos para este mix. Usa la búsqueda arriba.
                </p>
              </li>
            )}
          </ul>
          {isContainerFull && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-md rounded-xl z-10 pointer-events-auto p-4"
              aria-live="polite"
            >
              <div className="flex flex-col items-center gap-3 rounded-2xl border-2 border-primary/20 bg-gradient-to-b from-primary/10 to-white px-5 py-4 shadow-lg max-w-[15rem] text-center">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <Container className="h-6 w-6" aria-hidden />
                </div>
                <p className="text-sm font-semibold text-zinc-800 leading-snug">
                  Contenedor lleno
                </p>
                <p className="text-xs text-zinc-600 leading-snug">
                  No se pueden añadir más ítems a este contenedor.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Controls for a mix block: num containers, container type, fill bars (no inline search; search is in panel to the right). */
function MixBlockControls({
  block,
  blockIndex,
  blocks,
  patchBlocks,
  sectionRows,
  allItems,
  loadingMethods,
  add,
  brands,
  origins,
  originPorts,
  moqUnits,
  toast,
  readOnly = false,
  showQuotationDays = false,
  canEditContainerType,
  finalReadOnlyView = false,
}: {
  block: MixBlock;
  blockIndex: number;
  blocks: QuotationDocBlocks;
  patchBlocks: (b: QuotationDocBlocks) => Promise<void>;
  sectionRows: CartLineRow[];
  allItems: SourcingItem[];
  loadingMethods: { id: string; name: string }[];
  add?: (line: QuotationCartLine) => Promise<void>;
  brands: { id: string; name: string }[];
  origins: { id: string; name: string }[];
  originPorts: { id: string; name: string; origin_id?: string }[];
  moqUnits: { id: string; name: string }[];
  toast?: (t: { title: string; description?: string; variant?: 'destructive' }) => void;
  readOnly?: boolean;
  /** When true, show estimated_production_days and estimated_arrival_days for this mix block (admin quotation). */
  showQuotationDays?: boolean;
  /** When false, container type is shown but not editable (only admin when request is en_revision can edit). */
  canEditContainerType?: boolean;
  /** Vista de cotización final: mostrar cabecera de contenedor en modo lectura (sin inputs). */
  finalReadOnlyView?: boolean;
}) {
  const [patching, setPatching] = useState(false);
  const orderIds = useMemo(() => sectionRows.map((r) => r.line.sourcingItemId), [sectionRows]);
  const pendingBlockUpdates = useRef<Partial<MixBlock>>({});
  const blockFlushTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestBlocksRef = useRef(blocks);
  latestBlocksRef.current = blocks;

  const flushBlockUpdates = useCallback(() => {
    if (Object.keys(pendingBlockUpdates.current).length === 0) return;
    const nextMix = [...(latestBlocksRef.current.mix ?? [])];
    if (!nextMix[blockIndex]) return;
    nextMix[blockIndex] = { ...nextMix[blockIndex], ...pendingBlockUpdates.current };
    pendingBlockUpdates.current = {};
    if (blockFlushTimeout.current) {
      clearTimeout(blockFlushTimeout.current);
      blockFlushTimeout.current = null;
    }
    setPatching(true);
    patchBlocks({ ...latestBlocksRef.current, mix: nextMix }).finally(() => setPatching(false));
  }, [blockIndex, patchBlocks]);

  const updateBlock = useCallback(
    (updates: Partial<MixBlock>) => {
      Object.assign(pendingBlockUpdates.current, updates);
      if (blockFlushTimeout.current) clearTimeout(blockFlushTimeout.current);
      blockFlushTimeout.current = setTimeout(flushBlockUpdates, 1000);
    },
    [flushBlockUpdates]
  );

  useEffect(() => {
    return () => {
      if (blockFlushTimeout.current) clearTimeout(blockFlushTimeout.current);
      if (Object.keys(pendingBlockUpdates.current).length > 0) flushBlockUpdates();
    };
  }, [flushBlockUpdates]);

  const isFullContainerBlock = block.rows?.length === 1 && block.rows[0] && isFullContainerRow(block.rows[0] as MixBlockRow);
  const fillBars = useMemo(
    () => (isFullContainerBlock ? null : computeMixFillBars(block, sectionRows, allItems, loadingMethods)),
    [block, sectionRows, allItems, loadingMethods, isFullContainerBlock]
  );

  const updateNumContainers = useCallback(
    (n: number) => {
      const v = Math.max(1, n);
      if (isFullContainerBlock && block.rows?.length === 1) {
        const row = block.rows[0] as MixBlockRow;
        updateBlock({ num_containers: v, rows: [{ ...row, quantity_containers: v }] });
      } else {
        updateBlock({ num_containers: v });
      }
    },
    [isFullContainerBlock, block.rows, updateBlock]
  );

  // Contenedor completo: cantidad y tipo van por línea; no mostrar controles a nivel bloque.
  const showBlockContainerFields = !isFullContainerBlock;
  const effectiveContainerType = isFullContainerBlock && block.rows?.[0]
    ? (block.rows[0] as MixBlockRow).container_type ?? block.container_type
    : block.container_type;
  const effectiveNumContainers = isFullContainerBlock && block.rows?.[0]
    ? (block.rows[0] as MixBlockRow).quantity_containers ?? block.num_containers
    : block.num_containers;
  const canEditType = (canEditContainerType ?? true) && !patching && !readOnly && !finalReadOnlyView;

  const [productionWeeksText, setProductionWeeksText] = useState('');
  const [transportWeeksText, setTransportWeeksText] = useState('');
  const productionWeeksRef = useRef('');
  const transportWeeksRef = useRef('');
  const weeksDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  productionWeeksRef.current = productionWeeksText;
  transportWeeksRef.current = transportWeeksText;

  useEffect(() => {
    setProductionWeeksText(block.estimated_production_days != null ? String(Math.round(block.estimated_production_days / 7)) : '');
    setTransportWeeksText(block.estimated_arrival_days != null ? String(Math.round(block.estimated_arrival_days / 7)) : '');
  }, [block.estimated_production_days, block.estimated_arrival_days]);

  const scheduleWeeksSave = useCallback(
    (field: 'production' | 'transport') => {
      if (weeksDebounceRef.current) clearTimeout(weeksDebounceRef.current);
      weeksDebounceRef.current = setTimeout(() => {
        weeksDebounceRef.current = null;
        if (field === 'production') {
          const weeks = parseWeeksFromText(productionWeeksRef.current);
          updateBlock({ estimated_production_days: weeks != null ? weeks * 7 : undefined });
        } else {
          const weeks = parseWeeksFromText(transportWeeksRef.current);
          updateBlock({ estimated_arrival_days: weeks != null ? weeks * 7 : undefined });
        }
      }, WEEKS_INPUT_DEBOUNCE_MS);
    },
    [updateBlock]
  );

  useEffect(() => () => { if (weeksDebounceRef.current) clearTimeout(weeksDebounceRef.current); }, []);

  return (
    <div className="mt-3 space-y-3">
      <div className="flex flex-wrap items-center gap-4">
        {showBlockContainerFields && !finalReadOnlyView && (
          <>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-zinc-600">Contenedores:</label>
              <Input
                type="number"
                min={1}
                max={99}
                value={effectiveNumContainers}
                onChange={(e) => updateNumContainers(Number(e.target.value) || 1)}
                disabled={patching || readOnly}
                className="w-16 h-8 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-zinc-600">Tipo:</label>
              <select
                className="border rounded px-2 py-1 text-sm h-8 bg-white min-w-[100px] disabled:opacity-90 disabled:cursor-not-allowed"
                value={effectiveContainerType === 20 ? '20' : effectiveContainerType === 40 ? '40' : effectiveContainerType === '20REF' ? '20REF' : '40REF'}
                onChange={(e) => canEditType && updateBlock({ container_type: toContainerType(e.target.value) })}
                disabled={!canEditType}
              >
                {CONTAINER_OPTIONS.map((o) => (
                  <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
                ))}
              </select>
            </div>
          </>
        )}
        {showBlockContainerFields && finalReadOnlyView && (
          <div className="flex flex-wrap items-baseline gap-4 text-sm text-zinc-800">
            <div className="flex items-baseline gap-1">
              <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                Contenedores
              </span>
              <span className="font-semibold tabular-nums">
                {effectiveNumContainers.toLocaleString()}
              </span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                Tipo
              </span>
              <span className="font-medium">
                {CONTAINER_OPTIONS.find((o) => o.value === effectiveContainerType)?.label ?? '—'}
              </span>
            </div>
          </div>
        )}
        {showQuotationDays && !finalReadOnlyView && (
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-zinc-600">Semanas de producción:</span>
              <Input
                type="text"
                placeholder="—"
                className="w-24 h-9 text-sm"
                value={productionWeeksText}
                onChange={(e) => {
                  setProductionWeeksText(e.target.value);
                  scheduleWeeksSave('production');
                }}
                disabled={patching || readOnly}
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-zinc-600">Semanas de transporte:</span>
              <Input
                type="text"
                placeholder="—"
                className="w-24 h-9 text-sm"
                value={transportWeeksText}
                onChange={(e) => {
                  setTransportWeeksText(e.target.value);
                  scheduleWeeksSave('transport');
                }}
                disabled={patching || readOnly}
              />
            </div>
          </div>
        )}
        {showQuotationDays && finalReadOnlyView && (() => {
          const priceSummary = computeBlockPriceSummary(block, allItems);
          return (
            <div className="flex flex-wrap items-baseline gap-4 text-sm text-zinc-800">
              <div className="flex items-baseline gap-1">
                <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                  Semanas de producción
                </span>
                <span className="font-medium tabular-nums">
                  {block.estimated_production_days != null ? Math.round(block.estimated_production_days / 7) : '—'}
                </span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                  Semanas de transporte
                </span>
                <span className="font-medium tabular-nums">
                  {block.estimated_arrival_days != null ? Math.round(block.estimated_arrival_days / 7) : '—'}
                </span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                  Precio x Contenedor
                </span>
                <span className="font-semibold tabular-nums">
                  {priceSummary.pricePerContainerUsd != null
                    ? `USD ${priceSummary.pricePerContainerUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : 'a consultar'}
                </span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                  Precio Parcial
                </span>
                <span className="font-semibold tabular-nums">
                  {priceSummary.partialPriceUsd != null
                    ? `USD ${priceSummary.partialPriceUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : 'a consultar'}
                </span>
              </div>
            </div>
          );
        })()}
      </div>
      {fillBars && (
      <div className="space-y-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-zinc-600">
            <span>Llenado por peso</span>
            <span className="flex items-center gap-2">
              {fillBars.hasMissingWeight ? (
                <span className="text-amber-700">Peso a consultar</span>
              ) : (
                <>
                  {fillBars.totalWeightKg.toLocaleString()} / {fillBars.maxWeight.toLocaleString()} kg ({fillBars.pctWeight.toFixed(0)}%)
                  {fillBars.overWeight && (
                    <Badge variant="destructive" className="gap-1 text-xs">
                      <AlertTriangle className="h-3 w-3" />
                      Sobrepeso
                    </Badge>
                  )}
                </>
              )}
            </span>
          </div>
          {!fillBars.hasMissingWeight && (
            <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${fillBars.overWeight ? 'bg-amber-500' : 'bg-sky-500'}`}
                style={{ width: `${Math.min(fillBars.pctWeight, 100)}%` }}
              />
            </div>
          )}
        </div>
        <div className="space-y-1">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-zinc-600">
            <span>Llenado por espacio (m³)</span>
            <span className="flex items-center gap-2">
              {fillBars.hasMissingSpace ? (
                <span className="text-amber-700">Dimensiones caja a consultar</span>
              ) : (
                <>
                  {`${fillBars.totalSpaceUsed.toFixed(1)} / ${fillBars.spaceCapacity} m³`}
                  {' '}({fillBars.pctSpace.toFixed(0)}%)
                  {fillBars.overSpace && (
                    <Badge variant="destructive" className="gap-1 text-xs">
                      <AlertTriangle className="h-3 w-3" />
                      Sobrevolumen
                    </Badge>
                  )}
                </>
              )}
            </span>
          </div>
          {!fillBars.hasMissingSpace && fillBars.spaceCapacity > 0 && (
            <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${fillBars.overSpace ? 'bg-amber-500' : 'bg-emerald-500'}`}
                style={{ width: `${Math.min(fillBars.pctSpace, 100)}%` }}
              />
            </div>
          )}
        </div>
        {fillBars && fillBars.palletCapacity > 0 && sectionRows.length > 0 && (fillBars.totalPallets > 0 || fillBars.hasMissingPallets) && (
          <div className="space-y-1">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-zinc-600">
              <span>Llenado por pallets</span>
              <span className="flex items-center gap-2">
                {fillBars.hasMissingPallets ? (
                  <span className="text-amber-700">Cajas por pallet a consultar</span>
                ) : (
                  <>
                    {fillBars.totalPallets % 1 === 0 ? fillBars.totalPallets : fillBars.totalPallets.toFixed(1)} / {fillBars.palletCapacity} pallets ({fillBars.pctPallets.toFixed(0)}%)
                    {fillBars.overPallets && (
                      <Badge variant="destructive" className="gap-1 text-xs">
                        <AlertTriangle className="h-3 w-3" />
                        Exceso pallets
                      </Badge>
                    )}
                  </>
                )}
              </span>
            </div>
            {!fillBars.hasMissingPallets && (
              <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${fillBars.overPallets ? 'bg-amber-500' : 'bg-violet-500'}`}
                  style={{ width: `${Math.min(fillBars.pctPallets, 100)}%` }}
                />
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

/**
 * Canonical blocks view: sections (general + mix) with fill bars and lines.
 * Source of truth for layout and conditions is store/cart; this component is extracted from it.
 */
export function QuotationBlocksView({
  request,
  items,
  mode,
  readOnly: readOnlyProp,
  clientProps,
  adminProps,
  showOnlyAddBlock = false,
}: QuotationBlocksViewProps) {
  const readOnly = readOnlyProp ?? (mode === 'admin-view');
  const isClient = mode === 'client';
  const isAdminEdit = mode === 'admin-edit';
  const lastSectionRef = useRef<HTMLDivElement | null>(null);

  // Incluir bloques vacíos cuando hay clientProps (cliente o admin en revisión) para poder añadir productos y crear bloques mixtos.
  const sections = useMemo(
    () =>
      buildSectionsFromBlocks(request?.blocks, items, {
        includeEmptyBlocks: !!clientProps,
      }),
    [request?.blocks, items, clientProps]
  );

  /** Scroll to the newly created container after adding from "Agregar producto o contenedor"; wait for re-render. */
  const scrollToNewContainer = useCallback(() => {
    const scroll = () => lastSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(scroll, 450);
  }, []);

  const patchBlocksForView = useCallback(
    (blocks: QuotationDocBlocks) => {
      if (isClient && clientProps?.patchBlocks) {
        clientProps.patchBlocks(blocks);
      } else if (isAdminEdit && adminProps?.onPatchBlocks) {
        adminProps.onPatchBlocks(blocks);
      }
    },
    [isClient, isAdminEdit, clientProps, adminProps]
  );

  const patchBlocksAsync = useCallback(
    async (blocks: QuotationDocBlocks) => {
      if (isClient && clientProps?.patchBlocks) {
        await clientProps.patchBlocks(blocks);
      } else if (isAdminEdit && adminProps?.onPatchBlocks) {
        adminProps.onPatchBlocks(blocks);
      }
    },
    [isClient, isAdminEdit, clientProps, adminProps]
  );

  const [removingSectionKey, setRemovingSectionKey] = useState<string | null>(null);
  const showPriceRef = !!adminProps?.showFinalLinePrice;
  const priceRefEditing = mode === 'admin-edit' && !adminProps?.showFinalLinePrice;
  const showQuotationFields = isAdminEdit && !!adminProps?.onPatchBlocks && !!request?.blocks;

  /** Apply row-level quotation fields (price per box, days) to blocks and persist. */
  const handleRowQuotationFieldsBlur = useCallback(
    (rowSource: CartRowSource, fields: RowQuotationFields) => {
      if (!request?.blocks || !adminProps?.onPatchBlocks) return;
      const mix = [...(request.blocks.mix ?? [])];
      const block = mix[rowSource.mixBlockIndex];
      if (!block?.rows) return;
      const rows = [...block.rows];
      const r = rows[rowSource.rowIndex];
      if (!r) return;
      rows[rowSource.rowIndex] = {
        ...r,
        ...(fields.price_per_box_approx !== undefined && { price_per_box_approx: fields.price_per_box_approx }),
        ...(fields.estimated_production_days !== undefined && { estimated_production_days: fields.estimated_production_days }),
        ...(fields.estimated_arrival_days !== undefined && { estimated_arrival_days: fields.estimated_arrival_days }),
      };
      mix[rowSource.mixBlockIndex] = { ...block, rows };
      adminProps.onPatchBlocks({ ...request.blocks, mix });
    },
    [request?.blocks, adminProps]
  );

  if (showOnlyAddBlock && clientProps && !readOnly) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-sky-300 bg-sky-50/50 overflow-hidden">
        <div className="px-4 py-3 border-b border-sky-200 bg-sky-100/50">
          <h2 className="text-base font-bold text-zinc-900 flex items-center gap-2">
            <Plus className="h-5 w-5 text-sky-700" />
            Agregar producto o contenedor
          </h2>
          <p className="text-sm text-zinc-600 mt-0.5">
            Busca un producto: si es a <strong>contenedor completo</strong> se creará un bloque con ese producto. Si es por cajas o pallets, se creará un <strong>contenedor mixto</strong> y podrás añadir más productos del mismo origen, puerto y proveedor desde el bloque.
          </p>
        </div>
        <div className="p-4">
          <BlockProductSearch
            key="block-search-new-mix-only"
            context="new-mix"
            allItems={clientProps.allSourcingItems}
            orderIds={[]}
            moqUnits={clientProps.moqUnits}
            brands={clientProps.brands}
            origins={clientProps.origins}
            originPorts={clientProps.originPorts}
            add={clientProps.add}
            toast={clientProps.toast}
            readOnly={false}
            label="Buscar producto"
            placeholder="Buscar por producto, origen o puerto..."
            listBorderClass="border-sky-200"
            listHoverClass="hover:bg-sky-50"
            onAddedFromNewMix={undefined}
          />
        </div>
      </div>
    );
  }

  if (sections && sections.length > 0) {
    return (
      <div className="space-y-6 min-w-0 max-w-full">
        {sections.map((section, sectionIndex) => (
          <div
            key={section.key}
            ref={sectionIndex === sections.length - 1 ? lastSectionRef : undefined}
            className={`flex flex-col lg:flex-row gap-4 items-stretch transition-all duration-200 min-w-0 ${
              removingSectionKey === section.key ? 'opacity-0 scale-[0.98] pointer-events-none' : ''
            }`}
          >
            <div className="flex-1 min-w-0 rounded-2xl border-2 overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 via-white to-primary/5 shadow-lg">
              <div className="bg-gradient-to-r from-primary to-primary/80 px-4 py-3 min-h-[4.25rem] flex items-center text-primary-foreground shrink-0">
                <div className="flex items-center gap-2 w-full">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                    <Layers className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-bold text-sm leading-tight">{section.title}</h2>
                    {section.subtitle && (
                      <p className="text-[11px] opacity-90 mt-0.5 leading-tight">{section.subtitle}</p>
                    )}
                  </div>
                  {clientProps && !readOnly && section.mixBlockIndex != null && request?.blocks && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="shrink-0 bg-white/95 text-primary hover:bg-white border-0 shadow-sm h-8 px-2 text-xs font-semibold"
                      title="Eliminar contenedor completo"
                      disabled={removingSectionKey === section.key}
                      onClick={async () => {
                        setRemovingSectionKey(section.key);
                        try {
                          if (section.rows.length > 0 && clientProps.remove) {
                            const ids = section.rows.map((r) => r.line.sourcingItemId);
                            for (const id of ids) {
                              await clientProps.remove(id);
                            }
                          } else if (clientProps.patchBlocks) {
                            const mix = [...(request.blocks.mix ?? [])];
                            mix.splice(section.mixBlockIndex!, 1);
                            await clientProps.patchBlocks({ ...request.blocks, mix });
                          }
                          clientProps.toast?.({ title: 'Contenedor eliminado' });
                        } finally {
                          setRemovingSectionKey(null);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      Eliminar contenedor
                    </Button>
                  )}
                </div>
              </div>
            <div className="px-4 pt-4">
              {section.mixBlock != null && section.mixBlockIndex != null && request?.blocks && clientProps && (
                  <MixBlockControls
                    block={section.mixBlock}
                    blockIndex={section.mixBlockIndex}
                    blocks={request.blocks}
                    patchBlocks={patchBlocksAsync}
                    sectionRows={section.rows}
                    allItems={clientProps.allSourcingItems}
                    loadingMethods={clientProps.loadingMethods}
                    add={clientProps?.add}
                    brands={clientProps.brands}
                    origins={clientProps.origins}
                    originPorts={clientProps.originPorts}
                    moqUnits={clientProps.moqUnits}
                    toast={clientProps.toast}
                    readOnly={readOnly}
                    showQuotationDays={isAdminEdit && !!adminProps?.onPatchBlocks}
                    canEditContainerType={false}
                  />
                )}
                {section.mixBlock != null && section.mixBlockIndex != null && request?.blocks && !clientProps && (
                  <MixBlockControls
                    block={section.mixBlock}
                    blockIndex={section.mixBlockIndex}
                    blocks={request.blocks}
                    patchBlocks={patchBlocksAsync}
                    sectionRows={section.rows}
                    allItems={adminProps?.allSourcingItems ?? []}
                    loadingMethods={adminProps?.loadingMethods ?? []}
                    brands={[]}
                    origins={[]}
                    originPorts={[]}
                    moqUnits={[]}
                    readOnly={readOnly}
                    showQuotationDays={isAdminEdit || !!adminProps?.showFinalLinePrice}
                    canEditContainerType={isAdminEdit && request?.status === 'en_revision'}
                    finalReadOnlyView={!!adminProps?.showFinalLinePrice}
                  />
                )}
              <ul className="pt-3 pb-4 space-y-3">
                {section.rows.map((row, rowIndex) => (
                  <CartLineCard
                    key={row.line.sourcingItemId}
                    line={row.line}
                    quantity={row.quantity}
                    unitLabel={row.unitLabel}
                    minQuantity={row.line.moqValue}
                    updateQuantity={clientProps?.updateQuantity}
                    remove={clientProps?.remove}
                    rowLoadType={section.mixBlock?.rows[rowIndex]?.row_load_type}
                    defaultLoadType={
                      section.mixBlockIndex != null && clientProps
                        ? clientProps.getDefaultLoadType(row.line.sourcingItemId)
                        : undefined
                    }
                    showLoadTypeSelector={section.mixBlock != null}
                    onRowLoadTypeChange={
                      section.mixBlockIndex != null && clientProps && mode === 'admin-edit'
                        ? (v) => clientProps.updateMixRowLoadType(section.mixBlockIndex!, rowIndex, v)
                        : undefined
                    }
                    disabled={readOnly}
                    showPriceRef={showPriceRef}
                    priceRefEditing={priceRefEditing}
                    finalReadOnlyView={!!adminProps?.showFinalLinePrice}
                    pricePerBoxApprox={row.price_per_box_approx}
                    estimatedProductionDays={row.estimated_production_days}
                    estimatedArrivalDays={row.estimated_arrival_days}
                    showQuotationFields={showQuotationFields}
                    showQuotationDaysOnRow={false}
                    onQuotationFieldsBlur={
                      row.rowSource ? (fields) => handleRowQuotationFieldsBlur(row.rowSource!, fields) : undefined
                    }
                    hideMoqInSubtitle
                  />
                ))}
              </ul>
              </div>
            </div>
            {clientProps && section.mixBlock != null && section.mixBlockIndex != null && !isFullContainerBlock(section.mixBlock) && (
              <div className="min-w-0 lg:min-w-[16rem] lg:max-w-[24rem] shrink">
                <MixBlockSuggestionsPanel
                  key={`suggestions-${section.key}-${section.mixBlock.origin_id ?? ''}-${section.mixBlock.origin_port_id ?? ''}-${section.mixBlock.supplier_id ?? ''}`}
                  block={section.mixBlock}
                  mixBlockIndex={section.mixBlockIndex}
                  sectionRows={section.rows}
                  allItems={clientProps.allSourcingItems}
                  moqUnits={clientProps.moqUnits}
                  loadingMethods={clientProps.loadingMethods}
                  brands={clientProps.brands}
                  origins={clientProps.origins}
                  originPorts={clientProps.originPorts}
                  add={clientProps.add}
                  toast={clientProps.toast}
                  readOnly={readOnly}
                  itemIdToFamilyId={clientProps.itemIdToFamilyId}
                  mixOptions={clientProps.mixOptions}
                />
              </div>
            )}
          </div>
        ))}
        {/* Agregar producto o contenedor: búsqueda única; contenedor completo (MOQ 1 cont.) o mixto según el producto. */}
        {clientProps && !readOnly && (
          <div className="rounded-2xl border-2 border-dashed border-sky-300 bg-sky-50/50 overflow-hidden">
            <div className="px-4 py-3 border-b border-sky-200 bg-sky-100/50">
              <h2 className="font-bold text-base text-zinc-900 flex items-center gap-2">
                <Plus className="h-5 w-5 text-sky-700" />
                Agregar producto o contenedor
              </h2>
              <p className="text-sm text-zinc-600 mt-0.5">
                Busca un producto: si es a <strong>contenedor completo</strong> se creará un bloque con ese producto (solo cantidad de contenedores). Si es por cajas o pallets, se creará un <strong>contenedor mixto</strong> y podrás añadir más productos del mismo origen, puerto y proveedor desde el bloque.
              </p>
            </div>
            <div className="p-4">
              <BlockProductSearch
                key="block-search-new-mix"
                context="new-mix"
                allItems={clientProps.allSourcingItems}
                orderIds={items.map((i) => i.sourcingItemId)}
                moqUnits={clientProps.moqUnits}
                brands={clientProps.brands}
                origins={clientProps.origins}
                originPorts={clientProps.originPorts}
                add={clientProps.add}
                toast={clientProps.toast}
                readOnly={readOnly}
                label="Buscar producto"
                placeholder="Buscar por producto, origen o puerto..."
                listBorderClass="border-sky-200"
                listHoverClass="hover:bg-sky-50"
                onAddedFromNewMix={scrollToNewContainer}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Sin bloques pero con permisos de edición (ej. admin en borrador): mostrar solo "Agregar producto o contenedor".
  if (clientProps && !readOnly) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-sky-300 bg-sky-50/50 overflow-hidden">
        <div className="px-4 py-3 border-b border-sky-200 bg-sky-100/50">
          <h2 className="text-base font-bold text-zinc-900 flex items-center gap-2">
            <Plus className="h-5 w-5 text-sky-700" />
            Agregar producto o contenedor
          </h2>
          <p className="text-sm text-zinc-600 mt-0.5">
            Busca un producto: si es a <strong>contenedor completo</strong> se creará un bloque con ese producto. Si es por cajas o pallets, se creará un <strong>contenedor mixto</strong> y podrás añadir más productos del mismo origen, puerto y proveedor desde el bloque.
          </p>
        </div>
        <div className="p-4">
          <BlockProductSearch
            key="block-search-no-sections"
            context="new-mix"
            allItems={clientProps.allSourcingItems}
            orderIds={[]}
            moqUnits={clientProps.moqUnits}
            brands={clientProps.brands}
            origins={clientProps.origins}
            originPorts={clientProps.originPorts}
            add={clientProps.add}
            toast={clientProps.toast}
            readOnly={false}
            label="Buscar producto"
            placeholder="Buscar por producto, origen o puerto..."
            listBorderClass="border-sky-200"
            listHoverClass="hover:bg-sky-50"
            onAddedFromNewMix={undefined}
          />
        </div>
      </div>
    );
  }

  // Flat list when no blocks (read-only or no clientProps)
  return (
    <ul className="space-y-3">
      {items.map((line) => (
        <CartLineCard
          key={line.sourcingItemId}
          line={line}
          quantity={line.quantity}
          unitLabel={line.moqUnitLabel}
          minQuantity={line.moqValue}
          updateQuantity={clientProps?.updateQuantity}
          remove={clientProps?.remove}
          disabled={readOnly}
          showPriceRef={showPriceRef}
          priceRefEditing={priceRefEditing}
          finalReadOnlyView={!!adminProps?.showFinalLinePrice}
        />
      ))}
    </ul>
  );
}
